import {
  Box,
  Button,
  Card,
  CardContent, Checkbox,
  Divider, FormControlLabel,
  Grid, Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ImageSettings, { Size } from '../../../models/BlockComponentSettings/ImageSettings';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import ImageScaleSlider from '../../Modals/ImageSettingsModal/components/ImageScaleSlider';
import BackgroundImagePreview from '../../Modals/ImageSettingsModal/components/BackgroundImagePreview';
import ImageModal from '../../Modals/ImageModal/ImageModal';
import { fitParent } from '../../../utils/image';
import config from '../../../config';

interface BasicImageSettingsModalProps {
  image?: BlockComponentImage;
  defaultImage?: BlockComponentImage;
  onChange: (image: BlockComponentImage) => void;
  noScaleControls?: boolean;
  isEditing: boolean,
}

const BasicImageSettingsComponent = ({
  image: blockComponentImage,
  defaultImage,
  onChange,
  noScaleControls = false,
  isEditing = true,
}: BasicImageSettingsModalProps) => {
  const [image, setImage] = useState<BlockComponentImage>(blockComponentImage);
  const [imageSize, setImageSize] = useState<Size>({ width: 0, height: 0 });
  const [imageUpdated, setImageUpdated] = useState<number>(0);
  const [parallaxBool, setParallaxBool] = useState(blockComponentImage?.settings?.parallaxBool ?? false);
  const [scaleBool, setScaleBool] = useState(blockComponentImage?.settings?.scaleBool ?? false);
  const [scale, setScale] = useState<number>(blockComponentImage?.settings?.scale ?? 1);

  const defaultImageLocal: BlockComponentImage = defaultImage ?? new BlockComponentImage();

  const handleImageUpdated = (image: BlockComponentImage) => {
    const localImage = { ...image };
    onChange(localImage);
  };

  // IMAGE SETTINGS
  const handleChangeImage = (value: string) => {
    setImage({ ...image, value });
    handleImageUpdated({ ...image, value });
  };
  const handleChangeZoom = (scale: number) => {
    image.settings.scale = scale;
    setScale(scale);

    onChange({ ...image, settings: { ...image.settings, scale } });
    // console.log('Image', image);
  };
  const handleChangeImageSettings = (settings: ImageSettings) => {
    handleImageUpdated({ ...image, settings: { ...settings } });
    setImage({ ...image, settings: { ...settings } });
  };

  const handleChangeAlternativeText = (alt: string) => {
    setImage({ ...image, alt });
    handleImageUpdated({ ...image, alt });
  };

  const cropSize = useMemo(() => fitParent({ width: 533, height: 300 }, imageSize, { width: 72, height: 40 }), [imageSize]);

  const setDefaultSettings = (image: BlockComponentImage) => {
    const settings = ImageSettings.getDefault();
    setImage({ ...image, settings });
    onChange({ ...image, settings });
    setImageUpdated((imageUpdated + 1));
    setScaleBool(false);
    setScale(1);
  };

  const handleDefaultImage = (image: BlockComponentImage) => {
    const tmpImage = config.defaultImageUrl;

    setImage({ ...image, value: tmpImage });
    setImageUpdated((imageUpdated + 1));
    handleImageUpdated({ ...image, value: tmpImage });
  };

  return (
    <>
      {isEditing && (
        <div className="row">
          <div className="col-xl-5">
            {noScaleControls && (
              <Card sx={{ width: '100%' }} className="mb-3">
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ height: '42px', display: 'flex', alignItems: 'center' }}>
                      <Typography className="col-form-label mb-1 text-sm" sx={{ fontWeight: '700' }}>
                        Alt (alternative text) settings
                      </Typography>
                    </Box>
                    <TextField
                      label="Alternative text when the image is not displayed"
                      defaultValue={image.alt}
                      onChange={(event) => handleChangeAlternativeText(event.target.value)}
                      size="small"
                      inputProps={{ translate: 'no', className: 'notranslate' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
            {!noScaleControls && (
              <Card sx={{ width: '100%' }}>
                <CardContent>
                  <Grid item sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => setDefaultSettings(image)}
                        sx={{ marginTop: '10px', marginBottom: '10px' }}
                      >
                        Reset
                      </Button>
                    </Box>
                  </Grid>
                  <Divider sx={{ width: '100%', my: 1 }} />
                  <ImageScaleSlider
                    value={scale}
                    handleChange={handleChangeZoom}
                    showScaleCheckbox={false}
                    scaleCheckboxBool={scaleBool}
                    handleScaleCheckboxChange={(v) => {
                      setScaleBool(v);
                      setScale(1);
                      handleChangeImageSettings({ ...image.settings, scaleBool: v, scale: 1 });
                    }}
                  />
                  <Divider sx={{ width: '100%', my: 1 }} />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={parallaxBool}
                        onClick={(event) => {
                          setParallaxBool(!parallaxBool);
                          handleChangeImageSettings({ ...image.settings, parallaxBool: !parallaxBool });
                        }}
                        name="parallaxeffect"
                        color="primary"
                      />
                    )}
                    label="Fixed Background"
                  />
                </CardContent>
              </Card>
            )}
          </div>

          <div className="col-xl-7 px-0">
            <div className="row g-0 mt-md-1 mt-lg-0">
              <BackgroundImagePreview
                image={image}
                scale={scale}
                crop={cropSize}
                handleChange={(settings) => {
                  handleChangeImageSettings(settings);
                }}
                handleImageSizeChange={setImageSize}
                key={imageUpdated}
              />
            </div>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
              <div className="d-flex align-items-center gap-2">
                <ImageModal
                  handleOnConfirm={handleChangeImage}
                  title="Select Image"
                  absoluteButton={false}
                />
              </div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleDefaultImage(defaultImageLocal)}
                sx={{ marginTop: '10px', marginBottom: '10px', minWidth: 127 }}
              >
                Reset Image
              </Button>
            </div>
          </div>

        </div>
      )}
    </>
  );
};

export default BasicImageSettingsComponent;
